import { useCallback } from "react";

/**
 * Hook para detectar y manejar imágenes pegadas desde el portapapeles
 * 
 * @param {Function} setAttachments - Función para actualizar el estado de attachments
 * @param {Object} options - Opciones de configuración
 * @param {number} [options.maxFiles] - Máximo número de archivos permitidos (opcional)
 * @param {Function} options.onError - Callback para manejar errores (opcional)
 * @param {boolean} options.disabled - Si está deshabilitado (default: false)
 * @returns {Function} Handler para el evento onPaste
 */
export const usePasteImage = (setAttachments, options = {}) => {
    const { maxFiles, onError, disabled = false } = options;

    const handlePaste = useCallback(
        (event) => {
            if (disabled) return;

            const clipboardData = event.clipboardData;
            if (!clipboardData || !clipboardData.items) return;

            const items = Array.from(clipboardData.items);
            const imageItems = items.filter((item) => item.type.startsWith("image/"));

            if (imageItems.length > 0) {
                event.preventDefault(); // Evitar que se pegue como texto

                imageItems.forEach((item) => {
                    const file = item.getAsFile();
                    if (file) {
                        setAttachments((prev) => {
                            if (maxFiles != null && prev.length >= maxFiles) {
                                if (onError) {
                                    onError(`Máximo ${maxFiles} archivos adjuntos permitidos`);
                                }
                                return prev;
                            }
                            return [...prev, file];
                        });
                    }
                });
            }
        },
        [setAttachments, maxFiles, onError, disabled]
    );

    return handlePaste;
};

/**
 * Hook para detectar y manejar una única imagen pegada desde el portapapeles
 * con recorte automático si no es cuadrada (ideal para fotos de perfil)
 * 
 * @param {Object} options - Opciones de configuración
 * @param {Function} options.onImageProcessed - Callback cuando la imagen está lista (file, previewUrl)
 * @param {Function} options.onCropMessage - Callback para mostrar mensaje de recorte (opcional)
 * @param {Function} options.onError - Callback para manejar errores (opcional)
 * @param {boolean} options.disabled - Si está deshabilitado (default: false)
 * @returns {Function} Handler para el evento onPaste
 */
export const usePasteSingleImage = (options = {}) => {
    const { onImageProcessed, onCropMessage, onError, disabled = false } = options;

    const handlePaste = useCallback(
        (event) => {
            if (disabled) return;

            const clipboardData = event.clipboardData;
            if (!clipboardData || !clipboardData.items) return;

            const items = Array.from(clipboardData.items);
            const imageItem = items.find((item) => item.type.startsWith("image/"));

            if (imageItem) {
                event.preventDefault();
                const file = imageItem.getAsFile();
                if (!file) return;

                const img = new window.Image();
                img.onload = () => {
                    if (img.width !== img.height) {
                        // Recortar al cuadrado más pequeño
                        const size = Math.min(img.width, img.height);
                        const canvas = document.createElement("canvas");
                        const ctx = canvas.getContext("2d");
                        canvas.width = size;
                        canvas.height = size;

                        const x = (img.width - size) / 2;
                        const y = (img.height - size) / 2;

                        ctx.drawImage(img, x, y, size, size, 0, 0, size, size);

                        canvas.toBlob((blob) => {
                            const croppedFile = new File([blob], "pasted-image.png", {
                                type: file.type || "image/png",
                            });
                            const previewUrl = URL.createObjectURL(croppedFile);
                            if (onImageProcessed) {
                                onImageProcessed(croppedFile, previewUrl);
                            }
                            if (onCropMessage) {
                                onCropMessage("Esta imagen no es cuadrada, por lo que ha sido recortada");
                            }
                        }, file.type || "image/png");
                    } else {
                        const previewUrl = URL.createObjectURL(file);
                        if (onImageProcessed) {
                            onImageProcessed(file, previewUrl);
                        }
                        if (onCropMessage) {
                            onCropMessage(null);
                        }
                    }
                };
                img.onerror = () => {
                    if (onError) {
                        onError("Error al procesar la imagen pegada");
                    }
                };
                img.src = URL.createObjectURL(file);
            }
        },
        [onImageProcessed, onCropMessage, onError, disabled]
    );

    return handlePaste;
};

export default usePasteImage;
